/**
 * EmployeeInsightCard — detail view opened when a manager clicks an employee
 * row in TeamInsightsDashboard. Shows predicted vs. actual rating, the AI's
 * generated warning flags, a data-reliability banner, and KPI visualizations.
 */

import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import RemoveIcon from "@mui/icons-material/Remove";

import { employeePredictionDetailUrl, teamKpiAveragesUrl } from "../../../Service/Apis";
import { EmployeePredictionDetail, TeamKpiAverages } from "./Types/MLAnalyticsTypes";
import DeviationBadge from "./components/DeviationBadge";
import RemarksChips from "./components/RemarksChips";
import RatingComparisonChart from "./components/RatingComparisonChart";
import KpiDeviationChart from "./components/KpiDeviationChart";
import StarRating from "./components/StarRating";
import WhyThisRatingPanel from "./components/WhyThisRatingPanel";
import KeyInsightsPanel from "./components/KeyInsightsPanel";

interface EmployeeInsightCardProps {
  employeeId: string;
  onBack: () => void;
  /** See `TeamInsightsDashboard`'s `embedded` prop — avoids double top spacing when nested in a tab. */
  embedded?: boolean;
  /** KPI averages endpoint to compare this employee against — defaults to the manager's team average. */
  kpiAveragesUrl?: string;
  /** Label for the back button — defaults to "Back to Team Insights". */
  backLabel?: string;
}

const confidenceColor: Record<string, "success" | "warning" | "error" | "default"> = {
  High: "success",
  Medium: "warning",
  Low: "error",
  Unknown: "default",
};

const formatRating = (value: number | null | undefined): string =>
  value === null || value === undefined ? "—" : value.toFixed(2);

const EmployeeInsightCard: React.FC<EmployeeInsightCardProps> = ({
  employeeId,
  onBack,
  embedded = false,
  kpiAveragesUrl = teamKpiAveragesUrl,
  backLabel = "Back to Team Insights",
}) => {
  const [detail, setDetail] = useState<EmployeePredictionDetail | null>(null);
  const [teamKpis, setTeamKpis] = useState<TeamKpiAverages | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("token");
        const headers = { Authorization: `Bearer ${token}` };

        const [detailResponse, kpiResponse] = await Promise.all([
          axios.get(employeePredictionDetailUrl(employeeId), { headers }),
          axios.get(kpiAveragesUrl, { headers }),
        ]);

        setDetail(detailResponse.data);
        setTeamKpis(kpiResponse.data);
      } catch (err) {
        setError("Failed to load employee performance insights.");
      } finally {
        setLoading(false);
      }
    };

    fetchDetail();
  }, [employeeId, kpiAveragesUrl]);

  const gapArrow = () => {
    if (!detail || detail.prediction_gap === null || detail.prediction_gap === undefined) {
      return <RemoveIcon fontSize="small" />;
    }
    if (detail.prediction_gap > 0) return <ArrowUpwardIcon fontSize="small" color="success" />;
    if (detail.prediction_gap < 0) return <ArrowDownwardIcon fontSize="small" color="error" />;
    return <RemoveIcon fontSize="small" />;
  };

  return (
    <Box sx={{ mt: embedded ? 0 : 10, p: 3 }}>
      <Button startIcon={<ArrowBackIcon />} onClick={onBack} sx={{ mb: 2 }}>
        {backLabel}
      </Button>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && error && <Alert severity="error">{error}</Alert>}

      {!loading && !error && detail && (
        <Stack spacing={3}>
          <Card elevation={2}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap">
                <Box>
                  <Typography variant="h5" fontWeight={700}>
                    {detail.employee_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {detail.designation_name} &middot; {detail.employee_code}
                  </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                  <DeviationBadge category={detail.prediction_gap_category} />
                  <Chip
                    size="small"
                    label={`Risk: ${detail.risk_flag || "—"}`}
                    color={
                      detail.risk_flag === "High"
                        ? "error"
                        : detail.risk_flag === "Medium"
                        ? "warning"
                        : "success"
                    }
                    variant="outlined"
                  />
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          {detail.prediction_confidence_level === "Low" && (
            <Alert severity="warning">
              <strong>Low data confidence.</strong> This employee has low data completeness
              ({detail.feature_completeness_bucket || "low"}), so the AI prediction may be less
              reliable. Use manager judgement alongside this insight rather than relying on it
              alone.
            </Alert>
          )}
          {detail.prediction_confidence_level === "Unknown" && (
            <Alert severity="info">
              Not enough data was available to compute a confidence level for this employee.
            </Alert>
          )}

          <Grid container spacing={3}>
            {/* Left column: AI Predicted Manager Rating + Why this rating? */}
            <Grid item xs={12} md={5}>
              <Stack spacing={3}>
                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      AI Predicted Manager Rating
                    </Typography>
                    <Stack direction="row" alignItems="baseline" spacing={0.5} sx={{ mt: 1 }}>
                      <Typography variant="h3" fontWeight={800} color="primary.main">
                        {formatRating(detail.predicted_manager_rating)}
                      </Typography>
                      <Typography variant="h6" color="text.secondary">
                        / 5.00
                      </Typography>
                    </Stack>
                    <Box sx={{ mt: 0.5 }}>
                      <StarRating value={detail.predicted_manager_rating} size="medium" />
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 2 }}>
                      <Chip
                        size="small"
                        label={`Confidence: ${detail.prediction_confidence_level || "Unknown"}`}
                        color={confidenceColor[detail.prediction_confidence_level] || "default"}
                      />
                      <Typography variant="caption" color="text.secondary">
                        Based on {detail.non_null_feature_count ?? 0} data points
                      </Typography>
                    </Stack>

                    <Divider sx={{ my: 2 }} />

                    <Stack direction="row" spacing={4} alignItems="center">
                      <Box textAlign="center">
                        <Typography variant="caption" color="text.secondary">
                          Actual (Manager)
                        </Typography>
                        <Typography variant="h6" fontWeight={700}>
                          {formatRating(detail.manager_overall_rating)}
                        </Typography>
                      </Box>
                      <Box textAlign="center">
                        {gapArrow()}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {detail.prediction_gap === null || detail.prediction_gap === undefined
                            ? "—"
                            : detail.prediction_gap.toFixed(3)}
                        </Typography>
                      </Box>
                      <DeviationBadge category={detail.prediction_gap_category} />
                    </Stack>
                  </CardContent>
                </Card>

                <Card elevation={2}>
                  <CardContent>
                    <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                      Why this rating?
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 2 }}>
                      Top factors influencing the AI's prediction (SHAP feature contributions).
                    </Typography>
                    <WhyThisRatingPanel contributions={detail.feature_contributions} />
                  </CardContent>
                </Card>
              </Stack>
            </Grid>

            {/* Right column: real-data Key Insights vs. team average */}
            <Grid item xs={12} md={7}>
              <Card elevation={2} sx={{ height: "100%" }}>
                <CardContent>
                  <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                    <Typography variant="subtitle1" fontWeight={600}>
                      Key Insights
                    </Typography>
                  </Stack>
                  <Box sx={{ mb: 2 }}>
                    <RemarksChips remarks={detail.remarks} size="medium" />
                  </Box>
                  <KeyInsightsPanel
                    detail={detail}
                    teamAverages={teamKpis?.averages || {}}
                  />
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Feature completeness: {detail.feature_completeness_bucket || "—"}
                    {detail.feature_completeness_ratio !== null &&
                      detail.feature_completeness_ratio !== undefined &&
                      ` (${Math.round(detail.feature_completeness_ratio * 100)}% of expected data available)`}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Card elevation={2}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Self-Review vs. AI Predicted vs. Manager Actual
              </Typography>
              <Typography variant="caption" color="text.secondary" fontWeight={700} sx={{ display: "block", mb: 1 }}>
                Does the AI land closer to how the employee rated themselves, or to what the manager actually gave?
              </Typography>
              <RatingComparisonChart
                selfReview={detail.self_review_overall_rating}
                predicted={detail.predicted_manager_rating}
                actual={detail.manager_overall_rating}
              />
            </CardContent>
          </Card>

          <Card elevation={2}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                KPI Breakdown vs. Team Average
              </Typography>
              {teamKpis && teamKpis.kpi_fields.length > 0 ? (
                <KpiDeviationChart
                  kpiFields={teamKpis.kpi_fields}
                  employeeValues={detail as unknown as Record<string, number | null>}
                  teamAverages={teamKpis.averages}
                />
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Not enough team data to compute KPI averages.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Stack>
      )}
    </Box>
  );
};

export default EmployeeInsightCard;
